import Vue from "vue";
import Router from "vue-router";

import { BASE_URL, TokenType } from "@/helpers/Constants";
import { tokenPermissions } from '@/helpers/claims';
import { checkPermission } from "@/mixins/canWrite";

Vue.use(Router);

export default {
  methods: {
    processRequest(url = "", method = "get", data = {}, showSpinner = true, customURL = false, contentType = "application/json") {
      return this.requestApi(customURL ? url : BASE_URL + url, method, data, showSpinner, true, { contentType, skipAuthorization: customURL });
    },
    requestApi(url = "", method = "get", data = {}, showSpinner = true, showError = true, options = {}) {
      options = { contentType: "application/json", maxRetries: 2, ...options };
      if (showSpinner) {
        if (typeof (showSpinner) === "function") {
          showSpinner(true);
        } else {
          this.$store.commit("addRequest");
          this.$store.commit("setLoad", true);
        }
      }

      let authorization = this.$store.state.token.bearer;
      if (!authorization) authorization = sessionStorage.token || localStorage.token;
      else if ((this.$store.state.token.type & (TokenType.Autogenerated | TokenType.AdminUI | TokenType.Link)) === TokenType.AdminUI) localStorage.token = sessionStorage.token = authorization;
      // Create a Headers-like object that works in environments without the Fetch API
      const createHeaders = (init = {}) => {
        if (typeof Headers !== 'undefined') return new Headers(init);
        const map = Object.assign({}, init);
        map.set = (k, v) => { map[k] = v };
        map.forEach = (cb) => { Object.keys(map).forEach(k => cb(map[k], k)) };
        map.get = (k) => map[k];
        return map;
      };

      const headers = createHeaders({
        "Access-Control-Allow-Origin": location.origin
      });
      if (options.contentType && !(data instanceof FormData) && options.contentType.toLowerCase() !== 'multipart/form-data') {
        headers.set("Content-Type", options.contentType);
      }
      if (authorization && !options.skipAuthorization) headers.set("Authorization", authorization);
      if (!options.skipHeaders) {
        headers.set("RefUrl", location.href);
        headers.set("RefPage", this.$store.state.token.siteId ? 'dynamic' : this.$route.name?.toString());
      }

      const buildOptions = () => {
        const opts = {
          method: method.toUpperCase(),
          headers
        };
        if (method.toLowerCase() !== "get") {
          if (data instanceof FormData) {
            opts.body = data;
          } else if ((options.contentType || '').toLowerCase() === "application/json") {
            opts.body = JSON.stringify(data);
          } else {
            opts.body = data;
          }
        }
        return opts;
      };
      const defError = () => {
        let path = url;
        const api = url.split('/api');
        if (api.length > 1) path = api.pop();
        else {
          const lambda = url.split('amazonaws.com/');
          if (lambda.length > 1) path = lambda.pop();
        }
        return `Can not ${method} on ${path}`;
      };

      // Lightweight fetch fallback for environments without `fetch` (old TVs)
      const doFetch = (fetchUrl, opts) => {
        if (typeof fetch === 'function') return fetch(fetchUrl, opts);
        return new Promise((resolve, reject) => {
          try {
            const xhr = new XMLHttpRequest();
            xhr.open((opts && opts.method) || 'GET', fetchUrl, true);

            // copy headers
            if (opts && opts.headers) {
              try {
                if (typeof opts.headers.forEach === 'function') {
                  opts.headers.forEach((v, k) => { try { xhr.setRequestHeader(k, v) } catch (e) {} });
                } else {
                  for (const k in opts.headers) {
                    try { xhr.setRequestHeader(k, opts.headers[k]) } catch (e) {}
                  }
                }
              } catch (e) { /* ignore header copy issues */ }
            }

            xhr.onload = function() {
              const response = {
                ok: (xhr.status >= 200 && xhr.status < 300),
                status: xhr.status,
                text: () => Promise.resolve(xhr.responseText)
              };
              resolve(response);
            };
            xhr.onerror = function() { reject(new TypeError('Network request failed')) };
            xhr.ontimeout = function() { reject(new TypeError('Network request failed')) };

            // send body if present
            try {
              if (typeof opts !== 'undefined' && 'body' in opts) {
                xhr.send(opts.body == null ? null : opts.body);
              } else {
                xhr.send(null);
              }
            } catch (e) {
              // try sending as string if structured body caused issues
              try { xhr.send(JSON.stringify(opts.body)) } catch (e2) { xhr.send(null) }
            }
          } catch (err) { reject(err) }
        });
      };

      const attemptFetch = (attempt) => {
        return doFetch(url, buildOptions()).then(async(resp) => {
          if (!resp.ok) {
            if (resp.status === 401) this.$router.push('/admin/login');
            let text = await resp.text().catch(() => "");
            if (!text && showError) text = defError();
            try { if (text) text = JSON.parse(text); } catch { }
            if (location.origin.includes('localhost') || location.origin.includes('stage') || checkPermission(tokenPermissions.SuperAdmin, this.$store.state.token.permissions)) {
              console.warn(text.message ? text.message + '\n' + text.stackTrace : text);
            }
            if (showError && text) this.$awn.warning(text.message || text);
            return { ok: false, body: text };
          }

          const res = { ok: true, bodyText: await resp.text() };
          try { if (res.bodyText) res.body = JSON.parse(res.bodyText); } catch { res.body = res.bodyText }
          return res;
        }).catch((err) => {
          // retry only on network/CORS errors
          if (attempt < options.maxRetries) return attemptFetch(attempt + 1);
          if (showError) this.$awn.warning(defError());
          return { ok: false, body: err.message || defError() };
        });
      };

      const finalize = () => {
        if (showSpinner) {
          if (typeof (showSpinner) === "function") {
            showSpinner(false);
          } else {
            this.$store.commit("removeRequest");
            if (this.$store.state.activeRequests === 0) this.$store.commit("setLoad", false);
          }
        }
      };

      // Avoid using Promise.prototype.finally for compatibility with older devices
      return attemptFetch(0).then(
        (res) => { finalize(); return res },
        (err) => { finalize(); throw err }
      );
    }
  }
};
